<template>
  <div class="col-md-12 alert-message">
    <div v-if="alertMessage.message !== ''">
      <Message closable class="mb-2" role="alert" :severity="alertMessage.severity" @close="resetAlertMessage">
        {{ alertMessage.message }}
        <button
          v-if="alertMessage.showRenewActivation"
          type="button"
          class="btn btn-primary btn-request-activation"
          @click="toggleShowRenewActivation()"
        >
          Aktivierungslink erneut anfordern
        </button>
      </Message>
    </div>
  </div>
  <div v-if="isLoggedIn">
    <Profile :domainid="domainid" />
  </div>
  <div v-else-if="showRenewActivationForm">
    <wdw-dynbox title="Aktivierungslink neu anfordern">
      <RenewActivation :domainid="domainid" @close="toggleShowRenewActivation" />
    </wdw-dynbox>
  </div>
  <div v-else-if="showRequestPassword">
    <wdw-dynbox title="Neues Passwort anfordern">
      <RequestPassword :domainid="domainid" @close="onPasswordRequestClose" />
    </wdw-dynbox>
  </div>
  <div v-else-if="showRegistration">
    <wdw-dynbox title="Registrieren">
      <Registration :domainid="domainid" :privacyPageUri="privacyPageUri" :termsPageUri="termsPageUri" />
      <a @click="toggleShowRegistration(false)" class="switch-register-login-link to-login">
        Sie sind bereits registriert? Klicken Sie hier, um sich anzumelden.
      </a>
    </wdw-dynbox>
  </div>
  <div v-else>
    <wdw-dynbox title="Login">
      <Login
        :domainid="domainid"
        :handleEventsInParent="true"
        @open-registration="toggleShowRegistration(true)"
        @open-pw-request="toggleShowRequestPassword(true)"
      />
    </wdw-dynbox>
  </div>
</template>

<script>
import { userStore } from '@/store/user-store';
import { domainStore } from '@/store/domain-store';
import Login from '@/components/user/Login.vue';
import Profile from '@/components/user/Profile.vue';
import Registration from '@/components/user/Registration.vue';
import RenewActivation from '@/components/user/RenewActivation.vue';
import RequestPassword from '@/components/user/RequestPassword.vue';
import WdwDynbox from '@/components/general/WdwDynbox.vue';
import Message from 'primevue/message';
import useAuthentication from '@/composables/user/useAuthentication';
import useHashParams from '@/composables/general/useHashParams';
import { onBeforeMount, onMounted, reactive, ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { RepositoryFactory } from '@/api/repositoryFactory';

const AuthRepository = RepositoryFactory.get('auth');

/* 
Still in development
upgrade to a "user" component
Should Contain:
    -[x] Login 
     -[ ] refactor to FormControl fields
     -[ ] refactor to switch between login/registration
    -[ ] Registration
    -[ ] Request Password / Forgot Password
    -[ ] User Profile
    -[ ] Editing User Profile
    -[ ] Redirect after Account Activation from Server
    -[ ] Redirect after Password reset from Server
*/

export default {
  name: 'User',
  props: {
    domainid: {
      type: Number,
      required: true,
    },
    privacyPageUri: {
      type: String,
      required: false,
    },
    termsPageUri: {
      type: String,
      required: false,
    },
  },

  setup(props) {
    onBeforeMount(() => {
      domainStore.setDomainId(props.domainid);
    });
    const { initStorageAuthToken } = useAuthentication();
    const route = useRoute();
    const { deleteHashParam, setHashParams } = useHashParams(route);
    const showRegistration = ref(false);
    const showRenewActivationForm = ref(false);
    const showRequestPassword = ref(false);
    const alertMessage = reactive({
      message: '',
      severity: 'info',
      showRenewActivation: false,
    });

    //computed
    const user = computed(() => {
      return userStore.getState().user;
    });
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });

    function toggleShowRegistration(forceShow) {
      let toggleTo = !showRegistration.value;
      if (forceShow) {
        toggleTo = true;
      }

      // update hash params first (for watch on route to work correctly)
      if (toggleTo) {
        showRenewActivationForm.value = false;
        showRequestPassword.value = false;
        setHashParams({ register: true });
      } else {
        deleteHashParam('register');
      }

      // update actual var last
      showRegistration.value = toggleTo;
    }

    function toggleShowRequestPassword(show) {
      // update hash params first (for watch on route to work correctly)
      if (show) {
        showRenewActivationForm.value = false;
        showRegistration.value = false;
        setHashParams({ request_password: true });
      } else {
        deleteHashParam('request_password');
      }

      // update actual var last
      showRequestPassword.value = show;
    }

    function onPasswordRequestClose(message) {
      if (message) {
        alertMessage.message = message;
      }
      toggleShowRequestPassword(false);
    }

    function resetAlertMessage() {
      alertMessage.message = '';
      alertMessage.class = 'info';
      alertMessage.showRenewActivation = false;
      showRenewActivationForm.value = false;
    }

    function toggleShowRenewActivation(message) {
      console.log('toggling');
      // resetAlertMessage();
      showRenewActivationForm.value = !showRenewActivationForm.value;
      if (message) {
        alertMessage.message = message;
      } else {
        alertMessage.message = '';
      }
      alertMessage.class = 'info';
      alertMessage.showRenewActivation = false;
    }

    function activateUser(token) {
      resetAlertMessage();
      AuthRepository.activateUser(token)
        .then(response => {
          console.log(response);
          if (response && response.data.success && response.data.data) {
            alertMessage.message = 'Vielen Dank. Ihr Benutzerkonto ist jetzt aktiviert und Sie können sich einloggen.';
          } else {
            alertMessage.message =
              'Während der Aktivierung ist ein Fehler aufgetreten. Möchten Sie einen neuen Aktivierungslink anfordern?';
            alertMessage.class = 'warning';
            alertMessage.showRenewActivation = true;
          }
        })
        .catch(error => {
          console.error(error.response);
          let message = '';
          if (error.response && error.response.data && error.response.data.message) {
            message = error.response.data.message;
          }
          switch (message) {
            case 'user cannot be activated or is already activated':
              alertMessage.message = 'Nutzerin oder Nutzer konnte nicht aktiviert werden oder ist bereits aktiviert.';
              alertMessage.class = 'warning';
              break;
            default:
              alertMessage.message =
                'Der Aktivierungscode ist uns leider nicht bekannt. Möchten Sie einen neuen Aktivierungslink anfordern?';
              alertMessage.class = 'error';
              alertMessage.showRenewActivation = true;
          }
        });
    }

    function sendPassword(userid, token) {
      resetAlertMessage();
      AuthRepository.sendPassword(userid, token)
        .then(response => {
          console.log(response);
          if (response && response.data.success && response.data.data) {
            alertMessage.message = 'Ihnen wurde ein neues Passwort zugeschickt.';
          } else {
            alertMessage.message =
              'Beim Zurücksetzen des Passworts ist ein Fehler aufgetreten, bitten versuchen Sie es erneut';
            alertMessage.class = 'warning';
          }
        })
        .catch(error => {
          let message = '';
          if (error.response && error.response.data && error.response.data.message) {
            message = error.response.data.message;
          }
          switch (message) {
            case 'no user found':
              alertMessage.message =
                'Nutzerin oder Nutzer zum Zurücksetzen des Passworts konnte nicht gefunden werden. Bitte versuchen Sie es erneut.';
              alertMessage.class = 'error';
              break;
            default:
              alertMessage.message =
                'Beim Zurücksetzen des Passworts ist ein Fehler aufgetreten, bitten versuchen Sie es erneut';
              alertMessage.class = 'error';
          }
        });
    }

    // check hashparams here
    watch(route, (newRoute, oldRoute) => {
      if (
        newRoute.query.user_activate_token ||
        newRoute.query.send_pw_token ||
        (newRoute.query.register && !showRegistration.value) ||
        (newRoute.query.request_password && !showRequestPassword.value)
      ) {
        if (route.query.user_activate_token) {
          console.log('route.query.user_activate_token');
          console.log(route.query.user_activate_token);
          activateUser(route.query.user_activate_token);
          // show link to switch to login ( or directly show login? )

          // delete param
          deleteHashParam('user_activate_token');
        }
        if (route.query.send_pw_token) {
          if (route.query.userid) {
            console.log('route.query.send_pw_token');
            console.log(route.query.send_pw_token);
            sendPassword(route.query.userid, route.query.send_pw_token);
            // show link to switch to login ( or directly show login? )

            // delete param
            deleteHashParam('send_pw_token');
            deleteHashParam('userid');
          } else {
            console.error('send_pw_token provided but userid missing, doing nothing!');
            deleteHashParam('send_pw_token');
          }
        }
        if (route.query.register) {
          toggleShowRegistration(true);
        }
        if (route.query.request_password) {
          toggleShowRequestPassword(true);
        }
      }
    });

    return {
      user,
      isLoggedIn,
      showRegistration,
      toggleShowRegistration,
      showRequestPassword,
      toggleShowRequestPassword,
      onPasswordRequestClose,
      showRenewActivationForm,
      toggleShowRenewActivation,
      alertMessage,
      resetAlertMessage,
    };
  },

  components: {
    Login,
    Profile,
    Registration,
    RenewActivation,
    RequestPassword,
    WdwDynbox,
    Message,
  },
};
</script>

<style scoped>
.alert-message {
  display: contents;
}
</style>
